// ─── Research Judgment Engine ─────────────────────────────────────────────────
// Simulates the judgment real researchers exercise about what NOT to do.
// Evaluates the complete research output for overproduction, over-interpretation,
// artificial confirmation, and other patterns that distinguish AI output from
// genuine doctoral scholarship.

import { getDomainProfile } from './domainRealismProfiles'
import { validateStatisticalPlausibility } from './statisticalPlausibilityValidator'

// ── Judgment criteria ─────────────────────────────────────────────────────────

const JUDGMENT_CRITERIA = [
  {
    id: 'hyp_confirmation_rate',
    label: 'Hypothesis Confirmation Rate',
    evaluate: (ctx) => {
      const { narrative } = ctx
      if (!narrative) return null
      const total = narrative.supportedCount + narrative.partiallySupportedCount + narrative.notSupportedCount
      if (total === 0) return null
      const rate = narrative.supportedCount / total
      if (rate > 0.90 && total >= 3) {
        return {
          severity: 'high',
          message: `${Math.round(rate * 100)}% hypothesis confirmation is implausibly high for social science research. Real doctoral theses rarely confirm more than 60–70% of hypotheses. This pattern signals confirmation bias or data manipulation.`,
          recommendation: 'Accept some null findings as genuine contributions. Do not re-run analysis until "everything" is significant.',
        }
      }
      if (rate < 0.20 && total >= 3) {
        return {
          severity: 'medium',
          message: 'Very low confirmation rate — consider whether the theoretical model is misspecified or variables are poorly operationalised.',
          recommendation: 'Review variable operationalisation and theoretical alignment before concluding the model is invalid.',
        }
      }
      return null
    },
  },

  {
    id: 'analysis_count',
    label: 'Analysis Proliferation',
    evaluate: (ctx) => {
      const count = Object.keys(ctx.results || {}).length
      if (count > 6) {
        return {
          severity: 'medium',
          message: `${count} statistical analyses are present. Running too many analyses without prior theoretical motivation is a form of data dredging. Reviewers will question analytical selectivity.`,
          recommendation: 'Retain only analyses explicitly justified by research objectives and stated hypotheses. Remove exploratory tests not discussed in the methodology chapter.',
        }
      }
      return null
    },
  },

  {
    id: 'discussion_depth',
    label: 'Interpretation Depth',
    evaluate: (ctx) => {
      const { interpretation } = ctx
      if (!interpretation || interpretation.length < 200) {
        return {
          severity: 'high',
          message: 'Interpretation is absent or too thin. Statistical results without substantive interpretation are not a contribution.',
          recommendation: 'Every significant AND non-significant finding requires substantive discussion connecting it to theory, context, and practical implications.',
        }
      }
      return null
    },
  },

  {
    id: 'null_finding_treatment',
    label: 'Null Finding Treatment',
    evaluate: (ctx) => {
      const { narrative } = ctx
      if (!narrative) return null
      const nullOutcomes = narrative.hypothesisOutcomes?.filter(h => h.outcome === 'not_supported') || []
      const total = narrative.hypothesisOutcomes?.length || 0
      if (nullOutcomes.length > 0 && total > 0) {
        // Check whether interpretation actually addresses null findings
        const interp = ctx.interpretation || ''
        const addressesNull = /not.*significant|null finding|not supported|absence of|failed to|did not|non-significant/i.test(interp)
        if (!addressesNull) {
          return {
            severity: 'medium',
            message: `${nullOutcomes.length} non-significant finding(s) detected but the interpretation may not address them explicitly. Ignoring null findings is a common weakness in doctoral theses.`,
            recommendation: 'Devote explicit paragraph(s) to null findings: what they mean, why the hypothesis may have failed, and what alternative explanations exist.',
          }
        }
      }
      return null
    },
  },

  {
    id: 'contradiction_presence',
    label: 'Contradictory Findings',
    evaluate: (ctx) => {
      const { tensions, domain } = ctx
      const profile = getDomainProfile(domain)
      if (profile.contradictionLikelihood > 0.5 && (!tensions || tensions.length === 0)) {
        return {
          severity: 'medium',
          message: `${profile.label} research is expected to surface contradictions between quantitative and qualitative strands. Clean alignment between all findings and all expectations is theoretically suspect.`,
          recommendation: 'Introduce at least one substantive tension between the quantitative model and qualitative or contextual evidence. This adds credibility, not weakness.',
        }
      }
      return null
    },
  },

  {
    id: 'practical_significance',
    label: 'Practical vs Statistical Significance',
    evaluate: (ctx) => {
      const { results } = ctx
      if (!results?.ols) return null
      const coefs = Object.entries(results.ols.coefficients).filter(([k]) => k !== 'const')
      const tinyButSig = coefs.filter(([, c]) => c.significant && Math.abs(c.beta) < 0.05)
      if (tinyButSig.length > 0) {
        return {
          severity: 'medium',
          message: `${tinyButSig.length} predictor(s) are statistically significant but have extremely small effect sizes (|β| < .05). Large samples can make trivially small effects "significant". These should be flagged in the discussion as statistically but not practically significant.`,
          recommendation: 'Report effect sizes alongside p-values. Distinguish statistical significance from practical significance in the discussion.',
        }
      }
      return null
    },
  },

  {
    id: 'overreach_in_causation',
    label: 'Causal Language Check',
    evaluate: (ctx) => {
      const interp = ctx.interpretation || ''
      const causalWords = ['causes', 'caused', 'leads to', 'results in', 'proves that', 'demonstrates causality']
      const found = causalWords.filter(w => interp.toLowerCase().includes(w))
      if (found.length > 0) {
        return {
          severity: 'medium',
          message: `Potentially causal language detected: "${found.join('", "')}". Cross-sectional OLS regression establishes association, not causation.`,
          recommendation: 'Replace causal claims with associational language: "is associated with", "predicts", "is linked to". Reserve causal claims for experimental or longitudinal designs.',
        }
      }
      return null
    },
  },
]

// ── Authenticity validation ────────────────────────────────────────────────────

/**
 * Full export-level research authenticity validation.
 * Runs before final export to catch overproduction and AI-typical patterns.
 */
export function validateResearchAuthenticity(ctx) {
  const { results, narrative, interpretation, tensions, domain, variables } = ctx
  const profile = getDomainProfile(domain)

  const plausibility = validateStatisticalPlausibility(results || {}, domain)
  const judgmentIssues = []

  for (const criterion of JUDGMENT_CRITERIA) {
    const issue = criterion.evaluate(ctx)
    if (issue) {
      judgmentIssues.push({ criterion: criterion.label, ...issue })
    }
  }

  const highIssues    = judgmentIssues.filter(i => i.severity === 'high').length
  const mediumIssues  = judgmentIssues.filter(i => i.severity === 'medium').length
  const highPlausWarns = plausibility.warnings.filter(w => w.severity === 'high').length

  const authenticityScore = Math.max(0, Math.min(100,
    100
    - highIssues    * 20
    - mediumIssues  * 10
    - highPlausWarns * 15
    - Math.max(0, judgmentIssues.length - 3) * 5
  ))

  const overproductionWarnings = []
  if (Object.keys(results || {}).length > 5) {
    overproductionWarnings.push('More than 5 analysis types detected — trim to those explicitly theorised in the methodology chapter.')
  }
  if (profile.analysisSophistication === 'basic' && results?.moderation) {
    overproductionWarnings.push(`Moderation analysis is above the expected sophistication level for ${profile.label} research.`)
  }

  const suggestedSimplifications = []
  if (judgmentIssues.some(i => i.criterion === 'Analysis Proliferation')) {
    const avoidList = profile.avoidAnalyses || []
    if (avoidList.length > 0) suggestedSimplifications.push(`Remove: ${avoidList.join(', ')} (not typical for ${profile.label} research)`)
  }
  if (plausibility.recommendedAdjustments.length > 0) {
    suggestedSimplifications.push(...plausibility.recommendedAdjustments)
  }

  return {
    authenticityScore,
    plausibilityScore: plausibility.plausibilityScore,
    judgmentIssues,
    plausibilityWarnings: plausibility.warnings,
    overproductionWarnings,
    suggestedSimplifications,
    verdict: authenticityScore >= 80
      ? 'Research output is academically authentic and defensible.'
      : authenticityScore >= 60
        ? 'Moderate authenticity concerns — address judgment issues before submission.'
        : 'Significant authenticity concerns — output has characteristics of AI-generated over-confirmation. Revise before export.',
    ready: authenticityScore >= 70 && plausibility.plausibilityScore >= 65,
  }
}

/**
 * Quick judgment summary for display in Research Lab sidebar.
 */
export function quickJudgment(results, domain) {
  const profile = getDomainProfile(domain)
  const notes = []

  if (!results.ols) return { notes: ['Run OLS regression to enable judgment assessment.'], score: null }

  const coefs = Object.entries(results.ols.coefficients || {}).filter(([k]) => k !== 'const')
  const sigRate = coefs.length > 0 ? coefs.filter(([, c]) => c.significant).length / coefs.length : 0

  if (sigRate > profile.significanceRate + 0.25) {
    notes.push(`⚠ Significance rate (${Math.round(sigRate * 100)}%) exceeds domain norms (${Math.round(profile.significanceRate * 100)}%)`)
  }
  if (results.ols.r_squared > profile.typicalR2Range[1]) {
    notes.push(`⚠ R² = ${results.ols.r_squared} is above typical range for ${profile.label}`)
  }
  if (sigRate <= profile.significanceRate + 0.10 && results.ols.r_squared <= profile.typicalR2Range[1]) {
    notes.push('✓ Significance rate and R² are within domain-realistic ranges')
  }

  const nullCount = coefs.filter(([, c]) => !c.significant).length
  if (nullCount > 0) {
    notes.push(`✓ ${nullCount} non-significant predictor(s) — realistic null finding pattern`)
  }

  return { notes, score: null }
}
